import {ChangeEvent, forwardRef, KeyboardEvent, useCallback, useState} from 'react'
import {useDebouncedValue, useDidUpdate} from '@mantine/hooks'
import {InputProps} from '@yosmanyga/primitive-ui-spec'
import type {UnnormalizedProps as UnnormalizedStyleProps} from '@yosmanyga/core-style'
import {styled, SpacialFn, TextFn, VisualFn} from '@yosmanyga/web-style'

const Input = forwardRef<
  HTMLInputElement,
  InputProps & UnnormalizedStyleProps & {testId?: string}
>(({
  value: initialValue = '',
  placeholder, multi, focus, secure, capitalize,
  length, keyboard,
  delay = 0,
  onChange, onEnter, onKeyDown,
  className, testId
}, ref) => {
  const [value, setValue] = useState<HTMLInputElement['value']>(initialValue);
  const [debounced] = useDebouncedValue(value, delay);

  useDidUpdate(() => {
    if (initialValue === value) {
      return
    }

    setValue(initialValue)
  }, [initialValue])

  useDidUpdate(() => {
    onChange?.(debounced)
  }, [debounced])

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }, [])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (onEnter && event.key === 'Enter') {
      onEnter(value)

      return;
    }

    if (onKeyDown) {
      onKeyDown(event.key)
    }
  }, [
    value,
    onEnter, onKeyDown // Just to comply with the linter
  ])

  if (multi) {
    return <textarea
      defaultValue={value}
      rows={multi}
      onChange={(event) => {
        setValue(event.target.value)
      }}
      className={className}
      data-testid={testId}
    />
  }

  return <input
    ref={ref}
    value={value}
    placeholder={placeholder}
    type={buildType(secure)}
    autoFocus={focus}
    maxLength={length}
    onChange={handleChange}
    onKeyDown={handleKeyDown}
    onKeyUp={() => {
      if (keyboard) {
        if (
          keyboard === 'number'
        ) {
          setValue((prev) => {
            return prev.replace(/[^0-9]/g, '')
          });
        } else if (keyboard === 'decimal') {
          setValue((prev) => {
            return prev.replace(/[^0-9.]/g, '')
          });
        } else if (keyboard === 'phone') {
          setValue((prev) => {
            return prev.replace(/[^0-9+]/g, '')
          });
        }
      }

      if (capitalize) {
        if (capitalize === 'sentences') {
          setValue((prev) => {
            return prev.replace(/(^\w|\.\s+\w)/gi, (letter) => {
              return letter.toUpperCase()
            })
          });
        } else if (capitalize === 'words') {
          setValue((prev) => {
            return prev.replace(/\b\w/g, (letter) => {
              return letter.toUpperCase()
            })
          });
        } else if (capitalize === 'characters') {
          setValue((prev) => {
            return prev.toUpperCase()
          });
        }
      }
    }}
    className={className}
    data-testid={testId}
  />
})

const buildType = (secure?: boolean) => {
  switch (secure) {
    case true:
      return 'password'
    default:
      return 'text'
  }
}

const StyledInput = styled(Input)`
  ${({flex}) => SpacialFn.compileFlex(SpacialFn.normalizeFlex(flex))}
  ${({box}) => SpacialFn.compileBox(box)}
  ${({align}) => SpacialFn.compileAlign(align)}
  ${({margin}) => SpacialFn.compileMargin(SpacialFn.normalizeMargin(margin))}
  ${({padding}) => SpacialFn.compilePadding(SpacialFn.normalizePadding(padding))}
  ${({width}) => SpacialFn.compileWidth(SpacialFn.normalizeWidth(width))}
  ${({height}) => SpacialFn.compileHeight(SpacialFn.normalizeHeight(height))}

  border: 0;

  &:focus {
    outline: none;
  }

  ${({color}) => VisualFn.compileColor(color)}
  &::placeholder {
    ${({color, pseudo}) => VisualFn.compileColor(pseudo?.placeholder?.color || color)}
  }

  ${({background}) => VisualFn.compileBackground(VisualFn.normalizeBackground(background))}

  ${({font}) => TextFn.compileFont(font)}
  ${({line}) => TextFn.compileLine(line)}
`

// ${({border}) => VisualFn.compileBorder(VisualFn.normalizeBorder({
//   ...border,
//   fix: typeof border?.radius !== 'undefined'
// }))}

export default StyledInput
